import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomSnackbarService } from '@core/services/custom-snackbar.service';
import { GlobalStateService } from '@core/services/global.state.service';
import { CreateConversationStateService } from '@shared/components/conversation/create-conversation.state.service';
import { ConversationType } from '@shared/configs/conversation-types.config';
import { GLOBAL_SETTINGS } from '@shared/constants/global-settings';
import { OPPORTUNITY_STATUS_OPTIONS, PROBABILITY_OPTIONS } from '@shared/constants/select-options.constants';
import { AssigneeFormControl } from '@shared/controls/assignee-form-control';
import {
  CampaignsModel, ConversationModel, OpportunityCreateModel,
  SearchMemberOptionsModel, SearchUserOptionsModel
} from '@shared/models';
import { Observable, Subject } from 'rxjs';
import { getEndOfDate, getEndOfDateWithoutTimeStamp } from '@shared/utils';
import { OPPORTUNITY_CLOSED_STATUSES } from 'app/modules/conversations/models/conversation-status.enum';
import { CreateConversationCommonComponent } from '@shared/components/conversation/create-conversation-common.component';
import { takeUntil } from 'rxjs/operators';
import { FileTypeEnum } from '@shared/enums';
import { CmButtonComponent } from '@shared/components/cm-button/cm-button.component';
import { ConversationApiService, TaskApiService, UserGroupManagementApiService } from '@core/services/api.services';
import { EnailNotificationApiService } from '@core/services/api.services/email-notification-api.service';
import { UserManagementStateService } from 'app/modules/setup/user-management/user-management.state.service';
import { GroupTypeStringEnum } from '@shared/enums/group-type.enum';

@Component({
  selector: 'app-create-opportunity',
  templateUrl: './create-opportunity.component.html',
  styleUrls: ['../create-conversation/create-conversation.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CreateOpportunityComponent
  extends CreateConversationCommonComponent
  implements OnInit, AfterViewInit {
  defaultAssigneeUserName = new Subject<SearchUserOptionsModel>();
  conversationUsers$ = this.state.conversationUsers$();
  todayDate = new Date();
  opportunity = 'opportunity';
  // form: FormGroup;

  FileTypeEnum = FileTypeEnum;
  probabilityOptions = PROBABILITY_OPTIONS;
  reasonOptions: any[] = [];
  campaignOptions: CampaignsModel[];
  statusReasonOptions: any[] = [];
  GLOBAL_SETTINGS = GLOBAL_SETTINGS;
  selectedUser: any;

  referrers = [];
  viewInit = false;
  isRepRole = false;

  @ViewChild('referrersBtn', { read: CmButtonComponent })
  referrersBtnContainer: CmButtonComponent;
  constructor(
    fb: FormBuilder,
    state: CreateConversationStateService,
    toast: CustomSnackbarService,
    globalState: GlobalStateService,
    taskService: TaskApiService,
    conversationService: ConversationApiService,
    private _conversationApi: ConversationApiService,
    htmlElementRef: ElementRef,
    service: EnailNotificationApiService,
    private userstate: UserManagementStateService,
    private groupState: UserGroupManagementApiService
  ) {
    super(fb, state, globalState, toast, taskService, conversationService, htmlElementRef, service);
    this.getGroupByUserId(this.globalState.user.id);
    this.selectedUser = {
      label: this.globalState.user.firstName + ' ' + this.globalState.user.lastName,
      ref: this.globalState.user.id
    };
    setTimeout(() => {
      this.viewInit = true;
    }, 0);

    this.form.get('conversationType').patchValue(ConversationType.opportunity);

    this.statusOptions = OPPORTUNITY_STATUS_OPTIONS;

    this.form.addControl('expectedValue', this.fb.control(null));
    this.form.addControl(
      'assigneeId',
      new FormControl('')
    );
    this.form.addControl('appicationId', this.fb.control(null));
    this.form.addControl('declineReason', this.fb.control(null));
    this.form.addControl('expiryDate', this.fb.control(null));
    this.form.addControl('opportunityProbability', this.fb.control('Unknown'));
    this.form.addControl('expectedCloseDate', this.fb.control(null));
    this.form.addControl('campaignId', this.fb.control(-1));
    this.form.addControl('statusReasonId', this.fb.control(null));
  }

  ngOnInit(): void {


    super.ngOnInit();
    this.globalState
      .campaigns$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        this.campaignOptions = [...data];

        this.campaignOptions.push({
          value: -1,
          label: 'N/A',
        });
      });

    this._conversationApi.getOpportunityStatusReasons().subscribe((data) => {
      this.statusReasonOptions = data;
    });

    this._conversationApi.getOpportunityDeclinedReasons().subscribe((data) => {
      this.reasonOptions = this.normalizeDeclinedReasonOptions(data);
    });

    this.configureDeclineReasonValidation();

    this.form.get('assigneeId').valueChanges.subscribe((name) => {
      if (!name) {
        this.isRepRole = false;
        return;
      }

      if (name?.length >= GLOBAL_SETTINGS.minLengthToTriggerSearch) {
        this.state.getSearchedUserGroups(name, true);
      }
    });
  }

  get isStatusReasonMissing(): boolean {
    return this.form.get('status')?.value === 'Cancelled' && this.form.get('statusReasonId')?.value == null;
  }

  get isDeclineReasonMissing(): boolean {
    return this.form.get('status')?.value === 'Declined' && !this.form.get('declineReason')?.value;
  }

  ngAfterViewInit(): void {
    this.checkFormChanges();
  }

  getGroupByUserId(id: any): void {
    const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (pattern.test(id) === false) {
      this.groupState.getGroup(id).subscribe((response) => {
        const res = response;
        if (res.type === GroupTypeStringEnum.All ||
          response.type === GroupTypeStringEnum.CRMandRepresentativeRole ||
          response.type === GroupTypeStringEnum.ECMandRepresentativeRole) {
          this.isRepRole = true;
        }
      });
    }
  }

  onOptionSelected(event: any): void {
    if (event && event.hasOwnProperty('label')) {
      this.getGroupByUserId(event.ref);
    }
  }

  addReferrerInstance(links: SearchMemberOptionsModel[]): void {
    const acc = [];
    links.forEach((item) => {
      if (item.ref) {
        acc.push(item.ref);
      }
    });
    this.referrers = acc;
  }

  saveConversationType(): Observable<ConversationModel> {
    if (this.formLinks?.length) {
      this.formLinks.forEach(element => {
        if (element.isProspectInfo === true) {
          element.type = 'Prospect';
        } else if (element.isProspectInfo === false) {
          element.type = 'Member';
        }
        else {
          // eslint-disable-next-line
          element.type = element.type;
        }
      });
    }
    const status = this.form.value.status;
    const opportunity: OpportunityCreateModel = {
      applicationId: this.form.value.appicationId,
      subject: this.form.value.subject,
      description: this.form.value.description,
      status,
      productId: this.form.value.productId,
      assigneeId: this.selectedUser ? this.selectedUser.ref : '',
      attachments: this.form.value.attachments.map((x) => x.id),
      campaignId:
        this.form.value.campaignId === -1 ? null : this.form.value.campaignId,
      declineReason: this.form.value.declineReason?.value ?? this.form.value.declineReason,
      statusReasonId: this.form.value.statusReasonId,
      expectedCloseDate: !!this.form.value.expectedCloseDate
        ? getEndOfDateWithoutTimeStamp(new Date(this.form.value.expectedCloseDate))
        : null,
      expectedValue: this.form.value.expectedValue,
      expiryDate: !!this.form.value.expiryDate
        ? getEndOfDateWithoutTimeStamp(new Date(this.form.value.expiryDate))
        : null,
      opportunityProbability: this.form.value.opportunityProbability,
      links: this.formLinks,
      referrers: this.referrers,
      closedDate: OPPORTUNITY_CLOSED_STATUSES.includes(status) ? new Date().toISOString() : null,
    };
    return this.state.setOpportunity(opportunity);
  }

  getOptionText(option): any {
    return option?.label;
  }

  private configureDeclineReasonValidation(): void {
    const statusControl = this.form.get('status');
    const declineControl = this.form.get('declineReason');
    if (!statusControl || !declineControl) {
      return;
    }

    const updateValidators = (statusValue: any) => {
      if (statusValue === 'Declined') {
        declineControl.setValidators([Validators.required]);
      } else {
        declineControl.clearValidators();
        declineControl.setValue(null, { emitEvent: false });
      }
      declineControl.updateValueAndValidity({ emitEvent: false });
    };

    updateValidators(statusControl.value);

    statusControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(updateValidators);
  }

  private normalizeDeclinedReasonOptions(data: any[]): any[] {
    return (data || [])
      .map((item) => {
        if (!item) {
          return null;
        }

        if (typeof item === 'string') {
          return { value: item, label: item };
        }

        if ('value' in item && 'label' in item) {
          if (typeof item.value === 'string') {
            return item;
          }
          return { value: item.label, label: item.label };
        }

        if ('id' in item && 'name' in item) {
          return { value: item.id?.toString(), label: item.name };
        }

        if ('id' in item && 'label' in item) {
          return { value: item.id?.toString(), label: item.label };
        }

        return { value: item, label: item.toString() };
      })
      .filter(Boolean);
  }

}
